import { GetContainerDetailsInput, GetContainerDetailsOutput } from 'src/models/GetContainerDetails';
import { BatchMoveContainersInput, BatchMoveContainersOutput } from 'src/models/batchMoveContainers';
import { CloseContainerInput, CloseContainerOutput } from 'src/models/closeContainer';
import { SetRoutingDestinationInput, SetRoutingDestinationOutput } from 'src/models/setRoutingDestination';
import { CleanADirtyToteInput, CleanADirtyToteOutput } from 'src/models/cleanADirtyTote';
import BackendServiceCall from './backend-service-call';
import { ReceiveContainerInput, ReceiveContainerOutput } from 'src/models/receiveContainer';
import { PrintContainerLabelDetailsInput, PrintContainerLabelDetailsOutput } from 'src/models/PrintContainerLabelDetails';
import { ProcessContainerInput, ProcessContainerOutput } from 'src/models/processContainer';
import { InitRequest, InitResponse } from 'src/models/init-model';
import { GetSortTypesInput, GetSortTypesOutput } from 'src/models/GetSortTypes';
import { CreateContainerInput, CreateContainerOutput } from 'src/models/createContainer';
import { awsRum } from 'src/serviceClients/aws-rum/awsRum-Service';
import { RIVApiMethods } from 'src/constants/api-method-constants';
import { ClientSideMetricsConstants } from 'src/constants/client-side-metrics-constants';

/**
 * This class contains methods to trigger downstream API calls
 */
const INIT_URL = '/api/init';
const GET_CONTAINER_URL = '/api/getContainer';
const BATCH_MOVE_CONTAINER_URL = '/api/batchMoveContainers';
const CLOSE_CONTAINER_URL = '/api/closeContainer';
const RECEIVE_CONTAINER_URL = '/api/receiveContainer';
const PRINT_CONTAINER_LABEL_URL = '/api/printContainerLabel';
const PROCESS_CONTAINER_URL = '/api/processContainer';
const GET_SORT_TYPES_URL = '/api/getSortTypes';
const SET_ROUTING_DESTINATION_URL = '/api/setRoutingDestination';
const CLEAN_A_DIRTY_TOTE_URL = '/api/cleanADirtyTote';
const CREATE_CONTAINER_URL = '/api/createContainer';

const publishLatencyMetrics = (startTime: number, methodName: string, input: any, response: any) => {
    const latency = Date.now() - startTime;
    const mode = input.mode;
    const warehouse = input.warehouseId;
    const associate = input.associate;
    const DOT = '.';
    
    // Publish mode-specific event
    awsRum.recordEvent(ClientSideMetricsConstants.LATENCY + DOT + methodName + DOT + mode, {
        UIAction: methodName,
        Mode: mode,
        Warehouse: warehouse,
        Latency: latency,
        Associate: associate
    });
    
    return response;
};

const publishLatencyMetricsWithoutMode = (startTime: number, methodName: string, input: any, response: any) => {
    const latency = Date.now() - startTime;
    const warehouse = input?.warehouseId;
    const associate = input?.associate;
    const DOT = '.';
    
    awsRum.recordEvent(ClientSideMetricsConstants.LATENCY + DOT + methodName, {
        UIAction: methodName,
        Warehouse: warehouse,
        Latency: latency,
        Associate: associate
    });
    
    return response;
};

class RIVBFFService extends BackendServiceCall {
    public static async triggerInitAPI(initRequest: InitRequest) {
        const startTime = Date.now();
        const response: InitResponse = await this.makeApiRequest<InitRequest>(
            initRequest,
            INIT_URL,
            'GET'
        );
        return publishLatencyMetricsWithoutMode(startTime, RIVApiMethods.INIT, initRequest, response);
    }

    public static async triggerGetContainerDetailsAPI(input: GetContainerDetailsInput) {
        const startTime = Date.now();
        const response: GetContainerDetailsOutput = await this.makeApiRequest<GetContainerDetailsInput>(
            input,
            GET_CONTAINER_URL,
            'POST'
        );
        return publishLatencyMetrics(startTime, RIVApiMethods.GET_CONTAINER, input, response);
    }

    public static async triggerBatchMoveContainersAPI(input: BatchMoveContainersInput) {
        const startTime = Date.now();
        const response: BatchMoveContainersOutput = await this.makeApiRequest<BatchMoveContainersInput>(
            input,
            BATCH_MOVE_CONTAINER_URL,
            'POST'
        );
        return publishLatencyMetrics(startTime, RIVApiMethods.BATCH_MOVE_CONTAINERS, input, response);
    }

    public static async triggerCloseContainerAPI(input: CloseContainerInput) {
        const startTime = Date.now();
        const response: CloseContainerOutput = await this.makeApiRequest<CloseContainerInput>(
            input,
            CLOSE_CONTAINER_URL,
            'POST'
        );
        // The Close Container page names no mode; an unload close is timed under its mode, as unload's other calls are
        return (input.mode ? publishLatencyMetrics : publishLatencyMetricsWithoutMode)(startTime,
            RIVApiMethods.CLOSE_CONTAINER, input, response);
    }

    public static async triggerReceiveContainerAPI(input: ReceiveContainerInput) {
        const startTime = Date.now();
        const response: ReceiveContainerOutput = await this.makeApiRequest<ReceiveContainerInput>(
            input,
            RECEIVE_CONTAINER_URL,
            'POST'
        );
        return publishLatencyMetricsWithoutMode(startTime, RIVApiMethods.RECEIVE_CONTAINER, input, response);
    }

    public static async triggerPrintContainerLabelDetailsAPI(input: PrintContainerLabelDetailsInput) {
        const startTime = Date.now();
        const response: PrintContainerLabelDetailsOutput = await this.makeApiRequest<PrintContainerLabelDetailsInput>(
            input,
            PRINT_CONTAINER_LABEL_URL,
            'POST'
        );
        return publishLatencyMetricsWithoutMode(startTime, RIVApiMethods.PRINT_CONTAINER_LABEL, input, response);
    }

    public static async triggerProcessContainerAPI(input: ProcessContainerInput) {
        const startTime = Date.now();
        const response: ProcessContainerOutput = await this.makeApiRequest<ProcessContainerInput>(
            input,
            PROCESS_CONTAINER_URL,
            'POST'
        );
        return publishLatencyMetricsWithoutMode(startTime, RIVApiMethods.PROCESS_CONTAINER, input, response);
    }

    public static async triggerGetSortTypesAPI(input: GetSortTypesInput) {
        const startTime = Date.now();
        const response: GetSortTypesOutput = await this.makeApiRequest<GetSortTypesInput>(
            input,
            GET_SORT_TYPES_URL,
            'GET'
        );
        return publishLatencyMetricsWithoutMode(startTime, RIVApiMethods.GET_SORT_TYPES, input, response);
    }

    public static async triggerSetRoutingDestinationAPI(input: SetRoutingDestinationInput) {
        const startTime = Date.now();
        const response: SetRoutingDestinationOutput = await this.makeApiRequest<SetRoutingDestinationInput>(
            input,
            SET_ROUTING_DESTINATION_URL,
            'POST'
        );
        return publishLatencyMetricsWithoutMode(startTime, RIVApiMethods.SET_ROUTING_DESTINATION, input, response);
    }

    public static async triggerCleanADirtyToteAPI(input: CleanADirtyToteInput) {
        const startTime = Date.now();
        const response: CleanADirtyToteOutput = await this.makeApiRequest<CleanADirtyToteInput>(
            input,
            CLEAN_A_DIRTY_TOTE_URL,
            'POST'
        );
        return publishLatencyMetricsWithoutMode(startTime, RIVApiMethods.CLEAN_A_DIRTY_TOTE, input, response);
    }

    public static async triggerCreateContainerAPI(input: CreateContainerInput) {
        const startTime = Date.now();
        const response: CreateContainerOutput = await this.makeApiRequest<CreateContainerInput>(
            input,
            CREATE_CONTAINER_URL,
            'POST'
        );
        return publishLatencyMetrics(startTime, RIVApiMethods.CREATE_CONTAINER, input, response);
    }
}
 
export default RIVBFFService;